import { useTranslation } from 'react-i18next'
import StackedBar from '../../../components/ui/StackedBar'
import { STATUS_TONE_COLOR_VAR, statusTone, type ActionStatusOption } from '../../actions/types'

interface Props {
  /** Gruptaki toplam aksiyon (İptal/Arşivlendi hariç). */
  total: number
  /** Durum id (string anahtar) → adet; eksik durum 0 sayılır. */
  counts: Record<string, number>
  /** Gösterilecek durumlar — REFERANS sırası/adlarıyla (İptal/Arşivlendi backend'de dışlanır). */
  statuses: ActionStatusOption[]
}

/**
 * Toplantı grubu kartındaki aksiyon özeti (039 mockup): toplam aksiyon + durum dağılımı çubuğu + gösterge.
 *
 * <p>Çubuk, paylaşılan {@link StackedBar} ile render edilir (Reuse Mandate — Anasayfa panosu (053) da aynı
 * bileşeni kullanır; bu ekranda ikinci bir uygulama bırakılmadı). Segment genişlikleri, <b>gösterilen</b>
 * durumların adetleriyle orantılıdır (`max` verilmez → çubuk kendi içinde tam dolar). Durum → renk eşlemesi
 * {@link statusTone} + {@link STATUS_TONE_COLOR_VAR} ile <b>tek kaynaktan</b> gelir (statü rozetleriyle aynı
 * aile hue'su). Çubuğun taşıdığı bilgi, altındaki gösterge listesinde <b>metin</b> olarak da vardır.
 *
 * <p><b>Yalnız {@code statuses} ile verilen durumlar çizilir</b> (kullanıcı kararı 2026-08-28: kartta
 * Aksiyon Bekleniyor · Tamamlandı · Kapatıldı görünür). Listede karşılığı olmayan durumlar önceden tek bir
 * "Diğer" kovasında toplanıyordu; o kova kaldırıldı — adetleri çubuğa da göstergeye de girmez.
 *
 * <p>{@code total} ile göstergenin toplamı normalde <b>eşittir</b>: backend yalnız ANA görevleri sayar
 * (alt görev hariç, 2026-08-28) ve ana görevin statü kümesi tam olarak bu üç durumdur. Tek istisna,
 * kullanımdan kalkmış statüde (336/337) kalmış eski ana görevlerdir; onlar toplama girer ama çizilmez.
 */
export default function GroupActionStats({ total, counts, statuses }: Readonly<Props>) {
  const { t } = useTranslation()
  const items = statuses.map((s) => ({ ...s, count: counts[String(s.id)] ?? 0 }))

  const colorOf = (id: number) => STATUS_TONE_COLOR_VAR[statusTone(id)]

  return (
    <div className="mg-stats">
      <p className="mg-stats-total">
        <span className="mg-stats-num">{total}</span>
        <span className="mg-stats-label">{t('meetings.card.totalActions')}</span>
      </p>
      <StackedBar
        className="mg-stats-bar"
        ariaLabel={items
          .filter((i) => i.count > 0)
          .map((i) => `${i.name}: ${i.count}`)
          .join(', ')}
        segments={items.map((i) => ({
          key: String(i.id),
          label: i.name,
          value: i.count,
          color: colorOf(i.id),
        }))}
      />
      <ul className="mg-stats-legend">
        {items.map((i) => (
          <li key={i.id}>
            <span
              className="mg-stats-dot"
              style={{ background: colorOf(i.id) }}
              aria-hidden="true"
            />
            {`${i.name} ${i.count}`}
          </li>
        ))}
      </ul>
    </div>
  )
}
